import React, { useEffect, useRef, useState } from "react";
import CheckBox from "../CheckBox/CheckBox";
import Modal from "../Modal/Modal";
import Services from "../../services/Services";
import Spinner from "../Spinner/Spinner";
import "./ContractAgreementCheckbox.css";
import { temizMarkup } from "../SafeHtml/SafeHtml"; // FE-P2-2: sunucu HTML'i temizlenir

const pageContentCache = {};

// Kabul butonu, metnin en az bu oranı kaydırılarak okunmadan aktifleşmez
// (profil İletişim İzinleri modalıyla aynı kural).
const READ_RATIO = 0.8;

function getContentFromResponse(response) {
    const result = response?.data?.result;
    return result?.contentTr || result?.contentTR || result?.content || "";
}

export default function ContractAgreementCheckbox({
    checked,
    onAccepted,
    uri,
    title,
    children,
    token,
    lang = "tr",
    style,
    textStyle
}) {
    const [isOpen, setIsOpen] = useState(false);
    const [content, setContent] = useState(pageContentCache[`${uri}:${lang}`] || "");
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState("");
    const [scrolledEnough, setScrolledEnough] = useState(false);
    const bodyRef = useRef(null);

    const evaluateScroll = () => {
        const el = bodyRef.current;
        if (!el) return;
        // Metin kaydırmasız sığıyorsa okuma şartı sağlanmış sayılır.
        if (el.scrollHeight <= el.clientHeight + 4) {
            setScrolledEnough(true);
            return;
        }
        const progress = (el.scrollTop + el.clientHeight) / el.scrollHeight;
        if (progress >= READ_RATIO) setScrolledEnough(true);
    };

    // İçerik yüklenince (kaydırmasız sığma ihtimaline karşı) bir kez değerlendir.
    useEffect(() => {
        if (isOpen && content && !isLoading) evaluateScroll();
    }, [isOpen, content, isLoading]);

    const fetchContent = () => {
        const cacheKey = `${uri}:${lang}`;
        if (pageContentCache[cacheKey]) {
            setContent(pageContentCache[cacheKey]);
            return Promise.resolve(pageContentCache[cacheKey]);
        }

        setIsLoading(true);
        setError("");

        const resolveToken = token
            ? Promise.resolve(token)
            : Services.GetToken().then((res) => {
                if (!res?.data?.status || !res?.data?.result?.token) {
                    throw new Error("Token alınamadı");
                }
                return res.data.result.token;
            });

        return resolveToken
            .then((activeToken) => Services.GetWebPage(activeToken, uri, lang))
            .then((res) => {
                const nextContent = getContentFromResponse(res);
                if (!nextContent) throw new Error("Sözleşme içeriği alınamadı");
                pageContentCache[cacheKey] = nextContent;
                setContent(nextContent);
                return nextContent;
            })
            .catch((err) => {
                setError("Sözleşme metni yüklenemedi. Lütfen tekrar deneyin.");
            })
            .finally(() => setIsLoading(false));
    };

    const handleOpen = () => {
        setScrolledEnough(false);
        setIsOpen(true);
        fetchContent();
    };

    const handleAccept = () => {
        onAccepted(true);
        setIsOpen(false);
    };

    const handleCheckboxClick = (event) => {
        event.stopPropagation();
        if (checked) {
            onAccepted(false);
            return;
        }
        handleOpen();
    };

    return (
        <>
            <div
                className="contract-agreement-checkbox"
                style={style}
            >
                <span
                    className="contract-agreement-checkbox-control"
                    onClick={handleCheckboxClick}
                    role="checkbox"
                    aria-checked={checked}
                    tabIndex={0}
                    onKeyDown={(event) => {
                        if (event.key === "Enter" || event.key === " ") {
                            event.preventDefault();
                            handleCheckboxClick(event);
                        }
                    }}
                >
                    <CheckBox isSelected={checked} />
                </span>
                <span
                    className="contract-agreement-checkbox-text"
                    style={textStyle}
                    onClick={handleOpen}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(event) => {
                        if (event.key === "Enter" || event.key === " ") {
                            event.preventDefault();
                            handleOpen();
                        }
                    }}
                >
                    {children}
                </span>
            </div>

            <Modal
                isOpen={isOpen}
                title={title}
                hideTitle
                onClose={() => setIsOpen(false)}
                className="contract-agreement-modal"
                style={{ width: "760px", maxWidth: "92vw", height: "78vh", paddingBottom: "24px" }}
            >
                <div className="contract-agreement-modal-body" ref={bodyRef} onScroll={evaluateScroll}>
                    {isLoading && (
                        <div className="contract-agreement-loading">
                            <Spinner size={28} />
                        </div>
                    )}
                    {!isLoading && error && (
                        <div className="contract-agreement-error">{error}</div>
                    )}
                    {!isLoading && !error && content && (
                        <div
                            className="contract-agreement-content"
                            dangerouslySetInnerHTML={temizMarkup(content)}
                        />
                    )}
                </div>
                <div className="contract-agreement-actions">
                    <button
                        className="contract-agreement-accept-button"
                        disabled={isLoading || Boolean(error) || !content || !scrolledEnough}
                        title={!scrolledEnough ? "Metni okumadan kabul edemezsiniz" : undefined}
                        onClick={handleAccept}
                    >
                        Kabul Ediyorum
                    </button>
                </div>
            </Modal>
        </>
    );
}
